import type { ReactNode } from 'react';
import React, { Children, cloneElement } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isFunction } from 'lodash';
import { ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT, ARROW_UP, END, HOME } from 'Utils/keyCodes';
import { getTabsBackgroundClasses } from 'Components/utils/tabs/tabs.util';
import { withOpenAndScrollToHash } from 'Components/utils/tabs/tabs.hoc';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { ICONS } from 'Components/utils/icon/icons';
import { Text } from 'Components/frameworks/text/Text';
import { ActiveIndexes } from 'Components/frameworks/active-indexes/ActiveIndexes';
import type { IconName } from 'Components/frameworks/icon/Icon';
import 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { TabHeader } from 'Components/frameworks/tab-header/TabHeader';
import { Notification } from 'Components/frameworks/notification/Notification';
import { themes } from './Tabs.themes';
export type TabsItem = {
  customHeader?: ((arg0: any) => Node) | null | undefined;
  header?: {
    color?: Colors;
    iconName?: IconName;
    title: ReactNode;
  };
  id?: string;
  headerId: string;
  panelId: string;
  content: ReactNode;
};
type TabsProps = {
  areAllCollapsable?: boolean;
  bgColor?: Colors;
  className?: string;
  containerSize?: 'mini' | 'small' | 'medium' | 'large';
  forceEnableHeaderClick?: boolean;
  hasControlsOnTouch?: boolean;
  hasBackground?: boolean;
  isContainerWide?: boolean;
  isContentUnmountedOnCollapse?: boolean;
  id?: string;
  initialSelectedItem?: number;
  isScrollable?: boolean;
  items: Array<TabsItem>;
  onSelect?: (index: number) => void;
  onTabClick?: (headerId: string, index: number) => void;
  scrollBackwardLabel?: string;
  scrollForwardLabel?: string;
  slotLeft?: (activeIndexes?: Array<number>) => Node;
  slotRight?: (activeIndexes?: Array<number>) => Node;
  theme?: keyof typeof themes;
  message?: string;
};

const _Tabs = ({
  areAllCollapsable,
  bgColor,
  className,
  containerSize,
  forceEnableHeaderClick = false,
  hasControlsOnTouch,
  hasBackground,
  isContainerWide,
  isContentUnmountedOnCollapse,
  initialSelectedItem = 0,
  id,
  isScrollable,
  items = [],
  onSelect,
  onTabClick,
  scrollForwardLabel,
  scrollBackwardLabel,
  slotLeft,
  slotRight,
  theme = 'default',
  message,
}: TabsProps) => {
  const { isRTL } = useResponsive();

  if (!items.length) {
    return null;
  }

  const refsArray = [];
  const { thBody, thHeader, thList, thListItem, thScrollable, thSlot } = themes[theme];

  const tabUpdate = (idx, update) => {
    update(idx);
    refsArray[idx] && refsArray[idx].focus();
  };

  const onKeyDown = ({ e, index, update }) => {
    if (items.length > 1) {
      const lastItem = items.length - 1;

      switch (e.which) {
        case ARROW_RIGHT:
        case ARROW_DOWN:
          e.preventDefault();
          tabUpdate(index === lastItem ? 0 : index + 1, update);
          break;

        case ARROW_LEFT:
        case ARROW_UP:
          e.preventDefault();
          tabUpdate(index === 0 ? lastItem : index - 1, update);
          break;

        case HOME:
          e.preventDefault();
          tabUpdate(0, update);
          break;

        case END:
          e.preventDefault();
          tabUpdate(lastItem, update);
          break;

        default:
      }
    }
  };

  const tabsCount = items.filter(Boolean).length;
  return (
    <ActiveIndexes
      activeIndexes={[initialSelectedItem]}
      hasNoIndexEnabled={areAllCollapsable}
      onUpdate={onSelect}
    >
      {({ update, activeIndexes, lastIndex }) => {
        const els = items
          .filter(Boolean)
          .map(({ customHeader, header = {}, headerId, panelId }, index) => {
            const onClick = () => {
              tabUpdate(index, update);
              onTabClick?.(headerId, index);
            };

            const isActive = activeIndexes.includes(index);

            const setRef = (ref) => {
              refsArray[index] = ref;
            };

            const { title, ...rest } = header;
            const headerProps = {
              ...rest,
              className: thListItem,
              controlsId: panelId,
              id: headerId,
              isActive,
              isDisabled: forceEnableHeaderClick ? false : tabsCount <= 1,
              onClick: onClick,
              onKeyDown: (e) =>
                onKeyDown({
                  e,
                  index,
                  update,
                }),
              style:
                isRTL && isScrollable
                  ? {
                      direction: 'rtl',
                    }
                  : null,
              tabIndex: isActive || index === lastIndex ? 0 : -1,
            };

            if (isFunction(customHeader)) {
              return customHeader(headerProps, setRef);
            }

            return (
              title && (
                <TabHeader
                  {...headerProps}
                  key={headerId}
                  ref={setRef}
                  role="tab"
                  style={
                    isRTL && isScrollable
                      ? {
                          direction: 'rtl',
                        }
                      : null
                  }
                  theme={theme}
                >
                  {title}
                </TabHeader>
              )
            );
          });
        const tabsList = (
          <div className={classnames('flex w-full', thList)} role="tablist">
            {els}
          </div>
        );
        const hasActiveIndex = activeIndexes.length > 0 && !activeIndexes.includes(-1);
        return (
          <section
            className={classnames('relative isolate', className)}
            id={id}
            data-cs-override-id="Tabs" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
            data-testid="tabs"
          >
            <SiteContainer className="z-1" isWide={isContainerWide} size={containerSize}>
              <div className={classnames('flex', thHeader)}>
                {slotLeft && <div className={thSlot}>{slotLeft(activeIndexes)}</div>}
                {isScrollable ? (
                  <Scrollable
                    backwardLabel={scrollBackwardLabel}
                    className={thScrollable}
                    forwardLabel={scrollForwardLabel}
                    hasControlsOnTouch={hasControlsOnTouch}
                    navPosition="top"
                    theme="tabs"
                  >
                    {tabsList}
                  </Scrollable>
                ) : (
                  tabsList
                )}
                {slotRight && <div className={thSlot}>{slotRight(activeIndexes)}</div>}
              </div>
            </SiteContainer>
            {message && (
              <div className="justify-end md:flex">
                <Notification
                  className="mb-20 md:order-1 md:mb-0"
                  data-testid="specialOfferBlock"
                  iconName={ICONS.SPECIAL_OFFERS}
                >
                  <Text data-testid="specialOfferText" text={message} theme="text-sans-n1" />
                </Notification>
              </div>
            )}
            <div
              data-testid="tabs-items-container"
              className={classnames(
                {
                  [getTabsBackgroundClasses(hasBackground, bgColor)]: hasActiveIndex,
                },
                thBody,
              )}
            >
              {items.filter(Boolean).map(({ content, headerId, panelId }, index) => {
                const isActive = activeIndexes.includes(index);
                return (
                  (!isContentUnmountedOnCollapse || isActive) && (
                    <div
                      aria-hidden={!isActive}
                      aria-labelledby={headerId}
                      className={classnames({
                        'pointer-events-none invisible h-0 overflow-hidden': !isActive,
                        'animation-duration-500 animate-fadeIn': isActive,
                      })}
                      data-cs-override-id="Tabs-panel" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
                      id={panelId}
                      key={panelId}
                      role="tabpanel"
                      data-testid={`tabs-item-${index}-content`}
                    >
                      <SiteContainer isWide={isContainerWide} size={containerSize}>
                        {Children.map(content, (el, i) =>
                          el
                            ? cloneElement(el, {
                                key: `${panelId}-${i}`,
                                index,
                              })
                            : null,
                        )}
                      </SiteContainer>
                    </div>
                  )
                );
              })}
            </div>
          </section>
        );
      }}
    </ActiveIndexes>
  );
};

export const Tabs = withOpenAndScrollToHash(_Tabs);
